/**
 * SidebarUserProfile Component
 * Displays user avatar, name, and credits in the sidebar footer with dropdown menu
 */

import { UserAvatar } from "@/components/ui/user-avatar"
import { useCurrentUser } from "@/store/slices"
import { formatCompactUsdFromPcu } from "@/lib/credits-format"
import {
  ChevronsUpDown,
  LogOut,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuthActions } from "@/store/slices"
import { useCreditBalance } from "@/hooks/use-credits"
import { ThemeQuickSwitch } from "./theme-quick-switch"

/**
 * Get initials from user name
 */
const getInitials = (name: string): string => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

/**
 * SidebarUserProfile component props
 */
interface SidebarUserProfileProps {
  /** Whether to show collapsed (icon-only) version */
  collapsed?: boolean
}

/**
 * SidebarUserProfile component
 * Shows user avatar, name, and credits with dropdown menu for profile actions
 *
 * @returns JSX element with user profile display
 */
export function SidebarUserProfile({ collapsed = false }: SidebarUserProfileProps) {
  const user = useCurrentUser()
  const { logout } = useAuthActions()
  const { balance, userCap, isLoading: creditsLoading } = useCreditBalance()

  if (!user) {
    return null
  }

  const initials = getInitials(user.name || "User")

  // Calculate credit display
  const hasCap = userCap !== null
  const displayNum = hasCap ? userCap.remainingPcu : (
    typeof balance?.creditBalancePcu === 'string'
      ? parseFloat(balance.creditBalancePcu)
      : Number(balance?.creditBalancePcu || 0)
  )
  const isLow = displayNum < 100

  // Collapsed view - just avatar icon
  if (collapsed) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="p-1 rounded-lg border border-sidebar-border bg-background transition-colors hover:bg-sidebar-accent">
            <UserAvatar
              image={user.image}
              name={user.name}
              initials={initials}
              className="h-8 w-8"
              fallbackClassName="bg-primary text-primary-foreground text-xs font-medium"
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="center"
          side="right"
          sideOffset={8}
          className="w-56 rounded-lg"
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <UserAvatar
                image={user.image}
                name={user.name}
                initials={initials}
                className="h-8 w-8"
                fallbackClassName="bg-primary text-primary-foreground text-xs font-medium"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <ThemeQuickSwitch />
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => logout()} className="flex items-center gap-2">
            <LogOut className="h-4 w-4" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/*
          A raised card, not a tinted patch. `bg-sidebar-accent` is 96% grey on a
          98% rail — two percent of contrast, which reads as a smudge rather than
          a control, and sinks the one row here that opens a menu. Surface colour
          plus the rail's own border lifts it instead, and both tokens invert with
          the theme, so this stays a card in dark mode rather than a pale block.
        */}
        <button className="flex w-full items-center gap-3 rounded-lg border border-sidebar-border bg-background p-2 text-left transition-colors hover:bg-sidebar-accent">
          <UserAvatar
            image={user.image}
            name={user.name}
            initials={initials}
            className="h-10 w-10 flex-shrink-0"
            fallbackClassName="bg-primary text-primary-foreground text-sm font-medium"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{user.name}</p>
            {/*
              Labelled, not charted.

              The bar it replaces was only honest when a spend cap existed. With
              one it showed remaining/cap; without one it divided the balance by
              a hardcoded 100000 — so an unbounded workspace got a bar whose
              fullness meant nothing, and a negative balance pinned it to the 3%
              floor, drawing a sliver of progress against money already owed.
              The number was always the real content; "Current credit" says what
              it is, which the bar was standing in for.
            */}
            {!creditsLoading && balance && (
              <div className="mt-0.5 flex items-center gap-1">
                <span className="type-meta text-muted-foreground">Current credit</span>
                <span
                  className={`type-meta font-semibold ${isLow ? "text-destructive" : "text-foreground"}`}
                >
                  {formatCompactUsdFromPcu(displayNum)}
                </span>
              </div>
            )}
          </div>
          <ChevronsUpDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="right"
        sideOffset={4}
        className="w-56 rounded-lg"
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <UserAvatar
              image={user.image}
              name={user.name}
              initials={initials}
              className="h-8 w-8"
              fallbackClassName="type-meta bg-primary text-primary-foreground font-semibold"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{user.name}</p>
              <p className="type-meta text-muted-foreground truncate">{user.email}</p>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ThemeQuickSwitch />
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => logout()} className="flex items-center gap-2">
          <LogOut className="h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
